/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { call, select, delay, takeLatest, fork, put, takeLeading } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  TimeUtils,
  ActionRequest,
  ActionSuccess,
  AuthNToken,
  ActionFail,
  XError,
  AsyncActionState,
  Duration,
  HouseholdApi,
  AuthNErrors,
  Time,
  Milliseconds,
  NagraSettings,
  LEGACY,
  Ducks,
  LocalStorage,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';

// NOTE: this is a tech decision (not in the spec) in order to improve the token availability
// number of milliseconds before the current token expires that we will request a new token
const RENEW_BEFORE_EXPIRE_MILLIS = 5 * TimeUtils.MIN_SECS * TimeUtils.SEC_MILLIS;

// ACTIONS

export enum ActionType {
  INIT = 'playbackLicense/authN/INIT',
  REQUEST = 'playbackLicense/authN/REQUEST',
  SUCCESS = 'playbackLicense/authN/SUCCESS',
  FAIL = 'playbackLicense/authN/FAIL',
}

type Init = ActionRequest<ActionType.INIT>;
type Request = ActionRequest<ActionType.REQUEST>;
type Success = ActionSuccess<ActionType.SUCCESS, AuthNToken>;
type Fail = ActionFail<ActionType.FAIL>;
type Action = Init | Request | Success | Fail;

const Actions = {
  init: (): Init => ({ type: ActionType.INIT }),
  request: (): Request => ({ type: ActionType.REQUEST }),
  success: (payload: AuthNToken): Success => ({ type: ActionType.SUCCESS, payload }),
  fail: (error: XError): Fail => ({ type: ActionType.FAIL, error }),
};

export const ActionCreators = {
  init: Actions.init,
  request: Actions.request,
};

// SELECTORS
export const Selectors = {
  selectToken: (state: StoreState): string | undefined => state.playbackLicense.authN?.data?.token,
  selectTokenLastUpdate: (state: StoreState): Time | undefined => state.playbackLicense.authN?.data?.timestamp,
  isProcessing: (state: StoreState): boolean | undefined => state.playbackLicense.authN?.processing,
  error: (state: StoreState): XError | undefined => state.playbackLicense.authN?.error,
};

// the AuthN REDUCER
export type ReducerState = AsyncActionState<AuthNToken | undefined>;

const initialState: ReducerState = {
  data: undefined, // AuthNToken
  processing: false,
  processed: false,
  error: undefined,
};

export default function authN(state = initialState, action: Action): ReducerState {
  switch (action.type) {
    case ActionType.INIT:
    case ActionType.REQUEST:
      return {
        ...state,
        processing: true,
        processed: false,
        error: undefined,
      };

    case ActionType.SUCCESS:
      return {
        ...state,
        data: action.payload,
        processing: false,
        processed: true,
      };

    case ActionType.FAIL:
      return {
        ...state,
        data: undefined,
        processing: false,
        error: action.error,
      };

    default:
      return state;
  }
}

// SAGAS helpers

function isValidToken(authNToken: AuthNToken | undefined, duration: Duration): authNToken is AuthNToken {
  // existing and not expired token
  return authNToken !== undefined && TimeUtils.isAfterNow(TimeUtils.addDuration(authNToken.timestamp, duration));
}

function* getNewHouseholdDeviceToken(): SagaIterator<string> {
  // Ducks.Api.callApi may throw NetworkError
  const response: LEGACY.KalturaDeviceGetResponse = yield Ducks.Api.callApi(HouseholdApi.getDevice);
  const responseResult = response.result;

  if (LEGACY.isResultError(responseResult)) {
    throw XError.fromCode(AuthNErrors.validation.invalidResponse);
  }

  if (LEGACY.isResultSuccess(responseResult, 'KalturaHouseholdDevice')) {
    if (!responseResult.drm) {
      throw XError.fromCode(AuthNErrors.validation.invalidResponse);
    }

    if (!responseResult.drm.data) {
      throw XError.fromCode(AuthNErrors.validation.invalidDrmData);
    }

    return responseResult.drm.data;
  }

  throw XError.fromCode(AuthNErrors.generic.unknownError);
}

function getMillisToTokenExpire(creationDate: Time, duration: Duration): Milliseconds {
  const expirationDate = TimeUtils.addDuration(creationDate, duration);
  const millisToTokenExpire = TimeUtils.parse(expirationDate) - TimeUtils.nowUnixEpochMilliseconds();
  // in order to reduce the probability the token expires then retrieve a new one a bit early
  return Math.max(millisToTokenExpire - RENEW_BEFORE_EXPIRE_MILLIS, 0);
}

function* scheduleTokenRenew(currentToken: AuthNToken): SagaIterator {
  // for the token renew are considered:
  // - the token creation date
  // - the token duration (DMS param 'AuthNToken-neverExceed')
  const nagraSettings: NagraSettings = yield select(Ducks.Dms.Selectors.getNagraSettings);
  const { authNNeverExceed: authNDuration } = nagraSettings;
  const { timestamp: currentTokenCreationDate } = currentToken;

  const millisToExpire = getMillisToTokenExpire(currentTokenCreationDate, authNDuration);

  yield delay(millisToExpire);

  yield put(Actions.request());
}

// saves the token both in the store and storage
function* saveToken(token: string): SagaIterator {
  const newToken = { token, timestamp: TimeUtils.now() };
  yield put(Actions.success(newToken));
  yield fork(LocalStorage.storeAuthN, newToken);
}

// ACTIONS SAGAS

// this logic is part of the app starting process
// start the authN logic
function* initSaga(): SagaIterator {
  // 1. try to retrieve the authN token from the storage
  const storedAuthN: AuthNToken | undefined = yield call(LocalStorage.getAuthN);

  const nagraSettings: NagraSettings = yield select(Ducks.Dms.Selectors.getNagraSettings);
  const { authNRefresh } = nagraSettings;

  // 2. check if the token is still valid regarding the DMS param 'AuthNToken-refresh'
  if (isValidToken(storedAuthN, authNRefresh)) {
    // update the store with the existing valid token
    yield put(Actions.success(storedAuthN));
  } else {
    // 3. when the token is invalid then request a new one
    yield put(Actions.request());
  }
}

// run this logic when the device (STB) is added to the household domain
// this corresponds to the first valid device token a.k.a authN token
function* householdDeviceAddedSaga(action: Ducks.Household.AddDeviceSuccess): SagaIterator {
  const { token } = action.payload;
  if (token) {
    yield call(saveToken, token);
  }
}

// get and save the new token
function* requestTokenSaga(): SagaIterator {
  try {
    // if there isn't any errors then a valid token is returned
    const newToken = yield call(getNewHouseholdDeviceToken);
    yield call(saveToken, newToken);
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error, AuthNErrors.generic.unknownError)));
  }
}

// whenever the store is updated with a token
// it could be a fresh token (retrieved from the BE) or one from the storage
function* tokenUpdatedSaga(action: Success): SagaIterator {
  // schedule a renew task for the token
  yield call(scheduleTokenRenew, action.payload);
}

export function* saga(): SagaIterator {
  // when the app runs for the first time the device is added to the household and the device token may be returned
  yield takeLatest(Ducks.Household.ActionType.addDeviceToDomain.SUCCESS, householdDeviceAddedSaga);
  // whenever the app is launched, after the account details are loaded will init the authN logic

  yield takeLatest(ActionType.INIT, initSaga);

  // local sagas logic for local actions
  yield takeLeading(ActionType.REQUEST, requestTokenSaga);
  yield takeLeading(ActionType.SUCCESS, tokenUpdatedSaga);
}
